import { AssignmentsSection } from '@/policy/component/Assignment/AssignmentsSection';
import { useValidation } from '@/policy/contexts';
import { ConfirmedValidationStatus } from '@/policy/contexts/ValidationContext';
import {
  Amendment,
  AssignmentSection,
  CarGroupSection,
  FinancialDataSection as FinancialDataSectionData,
  Policy,
  Section,
  SectionType,
  Warranty,
} from '@/policy/types/PolicyCoverage';
import { ValidationResult } from '@/policy/types/ValidationResult';

import { AmendmentSection } from '../Amendment/AmendmentSection';
import { CarWarrantySection } from '../CarSection/CarWarrantySection';
import { FinancialDataSummarySection } from '../FinancialData/FinancialDataSummarySection';
import { PolicyCoverageSection } from '../PolicyCoverage/PolicyCoverageSection';
import { PolicyInformationSection } from '../PolicyInformation/PolicyInformationSection';

interface SectionFactoryProps {
  section: Section;
  showTitle: boolean;
  selectedSectionUuid: string | null;
  onSelectionChanged: (sectionUuid: string) => void;
  onAdvance: (sectionUuid: string) => void;
  tableRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
}

const shouldAdvance = (v: ConfirmedValidationStatus) =>
  v.status === 'COHERENT' || (v.status === 'ERROR' && v.confirmed);

const toValidationResult = (v: ConfirmedValidationStatus): ValidationResult => {
  if (v.status === 'ERROR') {
    return {
      status: 'ERROR',
      description: v.description,
      confirmed: v.confirmed,
    };
  }
  return { status: v.status };
};

export const SectionFactory = ({
  section,
  showTitle,
  selectedSectionUuid,
  onSelectionChanged,
  onAdvance,
  tableRefs,
}: SectionFactoryProps) => {
  const { findValidation, handleValidationChange } = useValidation();

  const makeSharedProps = (
    identifier: string,
    validationKind: SectionType,
  ) => ({
    ref: (el: HTMLDivElement | null) => {
      if (el) tableRefs.current.set(identifier, el);
      else tableRefs.current.delete(identifier);
    },
    validation: toValidationResult(findValidation(identifier)),
    onValidationChange: (v: ValidationResult) => {
      const withId: ConfirmedValidationStatus =
        v.status === 'ERROR'
          ? {
              identifier,
              status: 'ERROR',
              description: v.description,
              confirmed: v.confirmed,
            }
          : { identifier, status: v.status };
      handleValidationChange(identifier, validationKind, withId);
      if (shouldAdvance(withId)) onAdvance(identifier);
      else onSelectionChanged(identifier);
    },
    isSelected: selectedSectionUuid === identifier,
    onTableClick: onSelectionChanged,
  });

  switch (section.kind) {
    case 'car': {
      const carGroup = section as CarGroupSection;
      return (
        <CarWarrantySection
          section={carGroup}
          getValidation={(identifier) =>
            toValidationResult(findValidation(identifier))
          }
          onValidationChange={(identifier, validation) => {
            const withId: ConfirmedValidationStatus =
              validation.status === 'ERROR'
                ? {
                    identifier,
                    status: 'ERROR',
                    description: validation.description,
                    confirmed: validation.confirmed,
                  }
                : { identifier, status: validation.status };
            handleValidationChange(identifier, 'warranty', withId);
            if (shouldAdvance(withId)) {
              onAdvance(identifier);
            } else {
              onSelectionChanged(identifier);
            }
          }}
          selectedSectionUuid={selectedSectionUuid}
          onTableClick={onSelectionChanged}
          setRef={(identifier, el) => {
            if (el) {
              tableRefs.current.set(identifier, el);
            } else {
              tableRefs.current.delete(identifier);
            }
          }}
        />
      );
    }
    case 'warranty':
      return (
        <PolicyCoverageSection
          {...makeSharedProps(section.identifier, 'warranty')}
          situation={section as Warranty}
          showTitle={showTitle}
        />
      );
    case 'amendment':
      return (
        <AmendmentSection
          {...makeSharedProps(section.identifier, 'amendment')}
          section={section as Amendment}
        />
      );
    case 'policy':
      return (
        <PolicyInformationSection
          {...makeSharedProps(section.identifier, 'policy')}
          section={section as Policy}
        />
      );
    case 'assignment':
      return (
        <AssignmentsSection
          {...makeSharedProps(section.identifier, 'assignment')}
          section={section as AssignmentSection}
        />
      );
    case 'financialData':
      return (
        <FinancialDataSummarySection
          {...makeSharedProps(section.identifier, 'financialData')}
          section={section as FinancialDataSectionData}
        />
      );
  }
};
